import { AssetType, type AssetRef, type Link } from "#gen/bundles/v1/bundle_pb.js";
import { isFormAnchor } from "#lib/componentAnchors.js";
import { APP_ANCHOR_NAME, CONVERSION_ANCHOR_NAME, SECTION_1_ANCHOR_NAME, SECTION_2_ANCHOR_NAME, sanitizeAnchor, type BundleDag, type BundleNode } from "#lib/dag.js";
import type { HandleConnection, Node } from "@xyflow/svelte";

// Anchors owned by the node itself rather than a component inside it (e.g. a page's buttons).
export const RESTRICTED_ANCHORS = new Set([APP_ANCHOR_NAME, CONVERSION_ANCHOR_NAME, SECTION_1_ANCHOR_NAME, SECTION_2_ANCHOR_NAME]);
export const RESTRICTED_SOURCES = new Set([AssetType.EXPERIMENT, AssetType.UNSPECIFIED]);
export const NON_SELECTABLE_TYPES = new Set([AssetType.MODULE, AssetType.EXIT, AssetType.ENTRYPOINT_BATCH_REF, AssetType.GOTO]);
export const NON_DELETABLE_TYPES = new Set([AssetType.DECISION]);
const SPLIT_TYPES = new Set([AssetType.DECISION, AssetType.EXPERIMENT]);
const CENTERED_TYPES = new Set([AssetType.DECISION, AssetType.EXPERIMENT, AssetType.AGENT, AssetType.LOOKUP]);

// Allowed destination types per source type. The viewer's PASSTHROUGH_ASSET_TYPES is a related
// but separate split notion (it also counts LOOKUP).
const VALID_CONNECTIONS: Map<AssetType, Set<AssetType>> = new Map([
	[AssetType.PLACEMENT, new Set<AssetType>([AssetType.CODE, AssetType.CODE_V3, AssetType.CODE_BATCH, AssetType.SHORT_URL, AssetType.ENTRYPOINT_BATCH])],
	[AssetType.LANDING_PAGE, new Set<AssetType>([AssetType.GOTO, AssetType.PAGE, AssetType.PAGE_V2, AssetType.EXTERNAL_DESTINATION, AssetType.EXPERIMENT, AssetType.MODULE, AssetType.DECISION, AssetType.PAGE_CONTAINER, AssetType.SUITE_REF, AssetType.STATIC_ASSET, AssetType.POLICY_REF, AssetType.AGENT, AssetType.LOOKUP, AssetType.AD_UNIT])],
	[AssetType.PAGE_CONTAINER, new Set<AssetType>([AssetType.GOTO, AssetType.PAGE_V2, AssetType.EXTERNAL_DESTINATION, AssetType.EXPERIMENT, AssetType.MODULE, AssetType.DECISION, AssetType.LANDING_PAGE, AssetType.PAGE_CONTAINER, AssetType.SUITE_REF, AssetType.STATIC_ASSET, AssetType.POLICY_REF, AssetType.AGENT, AssetType.LOOKUP, AssetType.AD_UNIT])],
	[AssetType.CODE, new Set<AssetType>([AssetType.PAGE, AssetType.PAGE_V2, AssetType.LANDING_PAGE, AssetType.PAGE_CONTAINER, AssetType.SUITE_REF])],
	[AssetType.CODE_V3, new Set<AssetType>([AssetType.PAGE_V2, AssetType.LANDING_PAGE, AssetType.EXTERNAL_DESTINATION, AssetType.EXPERIMENT, AssetType.DECISION, AssetType.PAGE_CONTAINER, AssetType.SUITE_REF, AssetType.STATIC_ASSET, AssetType.AGENT, AssetType.LOOKUP])],
	[AssetType.CODE_BATCH, new Set<AssetType>([AssetType.PAGE_V2, AssetType.LANDING_PAGE, AssetType.EXTERNAL_DESTINATION, AssetType.EXPERIMENT, AssetType.DECISION, AssetType.PAGE_CONTAINER, AssetType.SUITE_REF, AssetType.STATIC_ASSET, AssetType.AGENT, AssetType.LOOKUP])],
	[AssetType.ENTRYPOINT_BATCH, new Set<AssetType>([AssetType.PAGE_V2, AssetType.LANDING_PAGE, AssetType.EXTERNAL_DESTINATION, AssetType.EXPERIMENT, AssetType.DECISION, AssetType.PAGE_CONTAINER, AssetType.SUITE_REF, AssetType.STATIC_ASSET, AssetType.AGENT, AssetType.LOOKUP])],
	[AssetType.ENTRYPOINT_BATCH_REF, new Set<AssetType>([AssetType.PAGE_V2, AssetType.LANDING_PAGE, AssetType.EXTERNAL_DESTINATION, AssetType.EXPERIMENT, AssetType.DECISION, AssetType.PAGE_CONTAINER, AssetType.SUITE_REF, AssetType.STATIC_ASSET, AssetType.AGENT, AssetType.LOOKUP])],
	[AssetType.PAGE_V2, new Set<AssetType>([AssetType.GOTO, AssetType.PAGE_V2, AssetType.EXTERNAL_DESTINATION, AssetType.MODULE, AssetType.DECISION, AssetType.LANDING_PAGE, AssetType.PAGE_CONTAINER, AssetType.SUITE_REF, AssetType.STATIC_ASSET, AssetType.POLICY_REF, AssetType.AGENT, AssetType.LOOKUP])],
	[AssetType.EXPERIMENT, new Set<AssetType>([AssetType.GOTO, AssetType.LANDING_PAGE, AssetType.EXTERNAL_DESTINATION])],
	[AssetType.SHORT_URL, new Set<AssetType>([AssetType.PAGE_V2, AssetType.LANDING_PAGE, AssetType.EXTERNAL_DESTINATION, AssetType.EXPERIMENT, AssetType.DECISION, AssetType.PAGE_CONTAINER, AssetType.SUITE_REF, AssetType.STATIC_ASSET, AssetType.LOOKUP])],
	[AssetType.MODULE, new Set<AssetType>([AssetType.GOTO, AssetType.PAGE_V2, AssetType.EXTERNAL_DESTINATION, AssetType.EXPERIMENT, AssetType.MODULE, AssetType.DECISION, AssetType.LANDING_PAGE, AssetType.PAGE_CONTAINER, AssetType.SUITE_REF, AssetType.STATIC_ASSET, AssetType.POLICY_REF, AssetType.LOOKUP])],
	[AssetType.DECISION, new Set<AssetType>([AssetType.GOTO, AssetType.PAGE_V2, AssetType.LANDING_PAGE, AssetType.EXTERNAL_DESTINATION, AssetType.EXPERIMENT, AssetType.MODULE, AssetType.PAGE_CONTAINER, AssetType.SUITE_REF, AssetType.STATIC_ASSET, AssetType.LOOKUP, AssetType.AD_UNIT])],
	[AssetType.AGENT, new Set<AssetType>([AssetType.PAGE_V2, AssetType.LANDING_PAGE, AssetType.EXTERNAL_DESTINATION, AssetType.EXPERIMENT, AssetType.MODULE, AssetType.PAGE_CONTAINER, AssetType.SUITE_REF, AssetType.STATIC_ASSET, AssetType.LOOKUP])],
	[AssetType.LOOKUP, new Set<AssetType>([AssetType.PAGE_V2, AssetType.LANDING_PAGE, AssetType.EXTERNAL_DESTINATION, AssetType.EXPERIMENT, AssetType.MODULE, AssetType.PAGE_CONTAINER, AssetType.SUITE_REF, AssetType.STATIC_ASSET, AssetType.LOOKUP])],
]);

export function canConnect(sourceType: AssetType | undefined, targetType: AssetType | undefined): boolean {
	if (sourceType === undefined || targetType === undefined) {
		return false;
	}
	return VALID_CONNECTIONS.get(sourceType)?.has(targetType) ?? false;
}

export function isSplitType(type: AssetType = AssetType.UNSPECIFIED): boolean {
	return SPLIT_TYPES.has(type);
}

export function isSplitNode(node: Node | undefined): boolean {
	const data = node?.data as AssetRef | undefined;
	return isSplitType(data?.type ?? AssetType.UNSPECIFIED);
}

export function isCenteredType(type: AssetType = AssetType.UNSPECIFIED): boolean {
	return CENTERED_TYPES.has(type);
}

export function isCenteredNode(node: Node | undefined): boolean {
	const data = node?.data as AssetRef | undefined;
	return isCenteredType(data?.type ?? AssetType.UNSPECIFIED);
}

export type AnchorTarget = { id: string; type: AssetType | undefined };

export type AddStrategy = { kind: "linkFromAnchor" } | { kind: "insertDecision" } | { kind: "addToExistingSplit"; splitId: string };

export function getAddStrategy(sourceType: AssetType | undefined, targets: AnchorTarget[]): AddStrategy {
	if (isSplitType(sourceType) || targets.length === 0) {
		return { kind: "linkFromAnchor" };
	}
	if (targets.length > 1 || !isSplitType(targets[0].type)) {
		return { kind: "insertDecision" };
	}
	return { kind: "addToExistingSplit", splitId: targets[0].id };
}

export function canAddAt(sourceType: AssetType | undefined, targets: AnchorTarget[], newType: AssetType | undefined): boolean {
	if (!canConnect(sourceType, newType)) {
		return false;
	}
	const strategy = getAddStrategy(sourceType, targets);
	switch (strategy.kind) {
		case "linkFromAnchor":
			return true;
		case "addToExistingSplit":
			return canConnect(targets[0].type, newType);
		case "insertDecision":
			return canConnect(sourceType, AssetType.DECISION) && canConnect(AssetType.DECISION, newType) && targets.every(target => canConnect(AssetType.DECISION, target.type));
	}
}

export function getAnchorTargets(connections: HandleConnection[], resolveNode: (id: string) => Node | undefined): AnchorTarget[] {
	return connections.map(connection => ({ id: connection.target, type: (resolveNode(connection.target)?.data as AssetRef | undefined)?.type }));
}

export function canHaveRules(source: BundleNode<AssetRef> | undefined, sourceAnchor: string | null | undefined): boolean {
	if (source?.parent) {
		return false;
	}
	// Count only links leaving this same anchor. Multiple links from one anchor only happen after
	// a decision split, so a multi-button page (many single-link anchors) correctly gets no rules.
	const anchor = sanitizeAnchor(sourceAnchor);
	return (source?.linksOut.filter(link => sanitizeAnchor(link.sourceAnchor) === anchor).length ?? 0) > 1;
}

// Action triggers belong on decision routes and form submissions, so hide them upstream of a
// decision unless the edge comes from a form (in-page form component or form module).
export function canHaveActions(dag: BundleDag<AssetRef>, source: BundleNode<AssetRef> | undefined, sourceAnchor: string | null | undefined, target: string): boolean {
	return comesFromForm(dag, source, sourceAnchor) || !leadsToDecision(dag, target);
}

// Selectable only if the edge can host rules or actions, or already carries some. Keeps
// pre-decision plumbing edges unselectable (matching production) without orphaning existing config.
export function canConfigureEdge(dag: BundleDag<AssetRef>, source: BundleNode<AssetRef> | undefined, link: Link): boolean {
	return canHaveRules(source, link.sourceAnchor) || canHaveActions(dag, source, link.sourceAnchor, link.destination) || link.conditions.length > 0 || link.actions.some(action => !action.disabled);
}

function comesFromForm(dag: BundleDag<AssetRef>, source: BundleNode<AssetRef> | undefined, sourceAnchor: string | null | undefined): boolean {
	// In-page form components route onward from a form anchor.
	if (isFormAnchor(sourceAnchor ?? undefined)) {
		return true;
	}
	// A form module exits via its "right" anchor, so detect it by its form content.
	// conversionComponent holds the indexed component id (e.g. "form-1"), so match it like an anchor.
	if (!source || source.ref.type !== AssetType.MODULE) {
		return false;
	}
	if (isFormAnchor(source.ref.conversionComponent)) {
		return true;
	}
	return [...(dag.modules[source.key]?.nodes.values() ?? [])].some(node => isFormAnchor(node.ref.conversionComponent));
}

function leadsToDecision(dag: BundleDag<AssetRef>, target: string | undefined): boolean {
	if (!target) {
		return false;
	}
	const visited: Set<string> = new Set();
	const queue: string[] = [target];
	while (queue.length) {
		const key = queue.shift()!;
		if (visited.has(key)) {
			continue;
		}
		visited.add(key);
		const node = dag.getNode(key);
		if (!node) {
			continue;
		}
		if (node.ref.type === AssetType.DECISION) {
			return true;
		}
		for (const link of node.linksOut) {
			queue.push(link.destination);
		}
	}
	return false;
}
